import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

/**
 * Diz se a sessão atual ainda deve o segundo fator.
 *
 * O DEFEITO QUE ISTO CORRIGE
 * -------------------------
 *
 * A tela de Configurações sabia cadastrar um fator TOTP desde sempre. O login
 * nunca pediu o código. Quem ativava a verificação em duas etapas ganhava um QR
 * code e a impressão de estar protegido: com a senha, e só com a senha, alguém
 * entrava na conta inteira. `challengeAndVerify` existia num lugar só do código,
 * e era o cadastro do fator — nunca a entrada.
 *
 * ATÉ ONDE ESTE PORTÃO PROTEGE
 * ----------------------------
 *
 * Ele roda no navegador. Contra quem controla o navegador, não vale nada — dá
 * para remover o componente e seguir em frente. Não é hipótese remota: é
 * exatamente a posição de quem roubou a senha e abriu o próprio Chrome.
 *
 * A defesa que vale é a do banco: política de RLS que exige `aal2`. Este portão
 * é a metade que se vê. A outra metade é `public.sessao_satisfaz_mfa()`, que
 * exige o segundo fator SOMENTE de quem tem fator cadastrado — por isso pode
 * ficar ligada sem trancar ninguém para fora.
 *
 * FALHA PARA QUE LADO
 * -------------------
 *
 * `getAuthenticatorAssuranceLevel()` lê a sessão local, sem rede: decodifica o
 * JWT que já está na memória. O único erro realista é não haver sessão.
 *
 * Ainda assim, se ele falhar, este portão **libera** em vez de barrar — e a
 * escolha é deliberada. Barrar transformaria um erro de decodificação em
 * bloqueio de 100% dos usuários (nenhum tem fator hoje), e não compraria
 * segurança nenhuma contra quem já controla o navegador. Quem precisa falhar
 * fechado é o banco, e lá ele falha.
 */
export type EstadoMfa = {
  /** Enquanto true, não decida nada — nem liberar, nem barrar. */
  carregando: boolean;
  /** A sessão tem fator verificado e ainda não passou por ele. */
  precisaElevar: boolean;
  /** Reavaliar depois de um desafio bem-sucedido. */
  revalidar: () => void;
};

export function useMfaGate(): EstadoMfa {
  const { session, loading } = useAuth();
  const [precisaElevar, setPrecisaElevar] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [tick, setTick] = useState(0);

  const revalidar = useCallback(() => setTick((t) => t + 1), []);

  // O token, não o objeto: a sessão é recriada a cada renderização do provider,
  // e depender dela faria este efeito rodar sem parar.
  const token = session?.access_token ?? null;

  useEffect(() => {
    if (loading) return;

    if (!token) {
      setPrecisaElevar(false);
      setCarregando(false);
      return;
    }

    let vivo = true;
    setCarregando(true);

    supabase.auth.mfa
      .getAuthenticatorAssuranceLevel()
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error || !data) {
          setPrecisaElevar(false);
          return;
        }
        // nextLevel só vira aal2 quando existe fator VERIFICADO. Fator abandonado
        // no meio do cadastro não conta — senão quem desistisse ficaria preso.
        setPrecisaElevar(data.currentLevel !== data.nextLevel && data.nextLevel === "aal2");
      })
      .catch(() => {
        if (vivo) setPrecisaElevar(false);
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });

    return () => {
      vivo = false;
    };
  }, [token, loading, tick]);

  return { carregando, precisaElevar, revalidar };
}
